import React, { useState, useEffect, useContext, useCallback } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { UserContext } from "../UserContext";
import { ThemeContext } from "../ThemeContext";
import api from "../api";
import { clearAuthStorage } from "../authStorage";
import ImageViewer from "../components/ImageViewer";
import { NavigationContext } from "../components/NavigationContext";
import { useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Clock, Trophy, TrendingUp, Target, Calculator as CalculatorIcon, X, Check } from "lucide-react";
import Calculator from "../components/Calculator";


const getNormalizedCategoryType = (name) => {
  const mapping = {
    advanced_math: "Advanced math",
    algebra: "Algebra",
    geometry_and_trigonometry: "Geometry and Trigonometry",
    problem_solving_and_data_analysis: "Problem Solving and Data Analysis",
    all_math: "All Math",
    reading: "Reading",
    writing: "Writing",
    sentence_completion: "Sentence Completion",
    all_english: "All English",
  };
  return mapping[name] || name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

// Glassmorphism Card with Animations
const Card = ({ children, className = '', delay = 0 }) => {
  const cardRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);
  const { isDarkMode } = useContext(ThemeContext);
  
  // Hover colors: Light mode: #06bae4, Dark mode: #ff9e42
  const hoverColor = isDarkMode ? '#ff9e42' : '#06bae4';
  
  // Convert hex to rgba for shadows
  const hexToRgba = (hex, alpha) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  return (
    <motion.div
      ref={cardRef}
      initial={{ opacity: 0, y: 50, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{
        duration: 0.6,
        delay: delay,
        type: "spring",
        stiffness: 100,
      }}
      whileHover={{
        scale: 1.02,
        y: -5,
        transition: { duration: 0.2 },
      }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      className={`relative backdrop-blur-xl bg-white/80 dark:bg-gray-800/80 rounded-xl sm:rounded-2xl md:rounded-3xl shadow-2xl p-3 sm:p-4 md:p-6 border transition-all duration-300 ${className}`}
      style={{
        boxShadow: isHovered
          ? `0 10px 40px rgba(0, 0, 0, 0.1), 0 0 0 2px ${hexToRgba(hoverColor, 0.3)}, 0 0 20px ${hexToRgba(hoverColor, 0.5)}, 0 0 40px ${hexToRgba(hoverColor, 0.3)}`
          : '0 10px 40px rgba(0, 0, 0, 0.1)',
        borderColor: isDarkMode ? 'rgba(55, 65, 81, 0.5)' : 'rgba(255, 255, 255, 0.2)',
      }}
    >
      {children}
    </motion.div>
  );
};

const PlayGamePage = ({ score, setScore }) => {
  const { isDarkMode } = useContext(ThemeContext);
  const [timer, setTimer] = useState(90);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState("");
  const [points, setPoints] = useState(0);
  const [status, setStatus] = useState("unanswered");
  const [sessionScore, setSessionScore] = useState(0);
  const [sessionInitialized, setSessionInitialized] = useState(false);
  const [showEndSessionModal, setShowEndSessionModal] = useState(false);
  const [backTriggered, setBackTriggered] = useState(false);
  const [sessionError, setSessionError] = useState(null);
  const [triggerApiCall, setTriggerApiCall] = useState(false);
  const [question, setQuestion] = useState(null);
  const [noMoreQuestions, setNoMoreQuestions] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [answer, setAnswer] = useState(null);
  const [questionId, setQuestionId] = useState(null);
  const [showSessionStartedMessage, setShowSessionStartedMessage] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, setUser } = useContext(UserContext);
  const [currentStreak, setCurrentStreak] = useState(0);
  const [textAnswer, setTextAnswer] = useState("");
  const trappedRef = useRef(false);
  const correctSfxRef = useRef(null);
  const wrongSfxRef = useRef(null);
  const [showWrongEffect, setShowWrongEffect] = useState(false);
  const [showCorrectEffect, setShowCorrectEffect] = useState(false);
  const [animatedPoints, setAnimatedPoints] = useState(0);
  const [isEndingSession, setIsEndingSession] = useState(false);
  const cancelledEndSessionRef = useRef(false);
  const [isCalculatorOpen, setIsCalculatorOpen] = useState(false);
  const [eliminatedOptions, setEliminatedOptions] = useState(new Set());

  // Get content_name and difficulty_level from location state
  const categoryName = location.state?.content_name;
  const difficultyLevel = location.state?.difficulty_level;

  // Close calculator if question changes to non-math
  useEffect(() => {
    if (question) {
      const categoryType = question.category_type?.toLowerCase() || '';
      const isMathCategory = categoryType === 'math' || 
                            categoryType === 'all math' ||
                            categoryType.includes('math') ||
                            categoryName?.toLowerCase().includes('math');
      if (!isMathCategory && isCalculatorOpen) {
        setIsCalculatorOpen(false);
      }
    }
  }, [question?.question_id, isCalculatorOpen, categoryName]);


  const {
    pendingNavigation,
    setPendingNavigation,
    promptEndSession,
    setPromptEndSession
  } = useContext(NavigationContext);

  // Optimized function to fetch question details
  const fetchQuestionDetails = useCallback(async (qid) => {
    try {
      const response = await api.get(`/api/solo/get_question/${qid}/`);
      return response.data;
    } catch (error) {
      console.error("Error fetching question details:", error);
      throw error;
    }
  }, []);

  // Preload correct answer sound
  useEffect(() => {
    try {
      if (typeof Audio !== 'undefined') {
        const audio = new Audio('/correct_answer.mp3');
        audio.preload = 'auto';
        audio.volume = 0.6;
        correctSfxRef.current = audio;
      }
    } catch {}
    return () => {
      try {
        if (correctSfxRef.current) {
          correctSfxRef.current.pause();
          correctSfxRef.current.src = '';
          correctSfxRef.current = null;
        }
      } catch {}
    };
  }, []);

  // Preload wrong answer buzz sound
  useEffect(() => {
    try {
      if (typeof Audio !== 'undefined') {
        const audio = new Audio('/wrong_answer.mp3');
        audio.preload = 'auto';
        audio.volume = 0.7;
        wrongSfxRef.current = audio;
      }
    } catch {}
    return () => {
      try {
        if (wrongSfxRef.current) {
          wrongSfxRef.current.pause();
          wrongSfxRef.current.src = '';
          wrongSfxRef.current = null;
        }
      } catch {}
    };
  }, []);

  const triggerCorrectEffects = async () => {
    // Show green tick mark animation
    setShowCorrectEffect(true);
    setTimeout(() => {
      setShowCorrectEffect(false);
    }, 1000); // Hide after 1 second
    
    // Confetti
    try {
      const mod = await import('canvas-confetti');
      const confetti = mod.default || mod;
      const colors = ['#36e338', '#47d2d3', '#22c55e', '#06b6d4'];
      confetti({
        particleCount: 120,
        spread: 70,
        startVelocity: 45,
        scalar: 0.9,
        zIndex: 10000,
        origin: { y: 0.6 },
        colors,
      });
      setTimeout(() => confetti({ particleCount: 60, angle: 60, spread: 55, startVelocity: 35, scalar: 0.9, zIndex: 10000, origin: { x: 0, y: 0.7 }, colors }), 100);
      setTimeout(() => confetti({ particleCount: 60, angle: 120, spread: 55, startVelocity: 35, scalar: 0.9, zIndex: 10000, origin: { x: 1, y: 0.7 }, colors }), 100);
    } catch {}
    // Sound
    try {
      const a = correctSfxRef.current;
      if (a) {
        a.currentTime = 0;
        a.play().catch(() => {});
      }
    } catch {}
  };

  const triggerWrongEffects = async () => {
    // Show X mark animation
    setShowWrongEffect(true);
    setTimeout(() => {
      setShowWrongEffect(false);
    }, 1000); // Hide after 1 second
    
    // Buzz sound
    try {
      const a = wrongSfxRef.current;
      if (a) {
        a.currentTime = 0;
        a.play().catch(() => {});
      }
    } catch {}
  };

  const hasInitializedRef = useRef(false);

  useEffect(() => {
    if (promptEndSession || pendingNavigation || isEndingSession) return; //Skip loading if modal will appear
    if (cancelledEndSessionRef.current) {
      cancelledEndSessionRef.current = false; // reset after skipping
      return;
    }
    if (hasInitializedRef.current) return; // Prevent re-initialization
    if (user && categoryName) {
      hasInitializedRef.current = true;
      setIsLoading(true);
      setSessionError(null);
      let session_id = localStorage.getItem(`sessionId_${categoryName}`) || null;
      if (location.state?.allowed && location.state?.fromChooseTopics) {
        localStorage.setItem(`sessionId_${categoryName}`, null);
        api.post('/api/solo/start_or_resume_session/', {
          content_name: categoryName
        })
          .then((res) => {
            console.log("Session initialized:", res.data);
            localStorage.setItem(`sessionStarted_${user?.user_id}_${categoryName}`, "true");
            localStorage.setItem(`sessionId_${categoryName}`, res.data.session_id);
            setSessionInitialized(true);
            setShowSessionStartedMessage(true);
            setTimeout(() => {
              setShowSessionStartedMessage(false);
            }, 3000);

            // Now that session is confirmed, fetch other data
            fetchScore();
            fetchQuestion();
          })
          .catch((err) => {
            console.error("Failed to start/resume session:", err);
            setSessionError("Unable to start session. Please return to the subject selection and try again.");
            setIsLoading(false);
            hasInitializedRef.current = false; // Reset on error
          });
      }
      else {
        setSessionInitialized(true);
        fetchScore();
        fetchQuestion();
      }
    }
  }, [user, categoryName, promptEndSession, pendingNavigation, isEndingSession]);

  useEffect(() => {
    if (user?.user_id) {
      api.get(`/api/user/get_user_profile/`)
        .then(res => {
          setCurrentStreak(res.data.current_streak || 0);
        })
        .catch(err => {
          console.error("Failed to fetch user profile:", err);
        });
    }
  }, [user]);

  // Animate points counter when question changes
  useEffect(() => {
    if (question && !isLoading) {
      const difficulty = question.difficulty_level?.toLowerCase();
      const targetPoints = difficulty === 'easy' ? 100 : difficulty === 'medium' ? 200 : difficulty === 'hard' ? 300 : 100;
      setAnimatedPoints(0);
      
      const duration = 800; // Animation duration in ms
      const steps = 30;
      const increment = targetPoints / steps;
      const stepDuration = duration / steps;
      
      let currentStep = 0;
      const timer = setInterval(() => {
        currentStep++;
        const newValue = Math.min(Math.round(increment * currentStep), targetPoints);
        setAnimatedPoints(newValue);
        
        if (currentStep >= steps) {
          clearInterval(timer);
          setAnimatedPoints(targetPoints);
        }
      }, stepDuration);
      
      return () => clearInterval(timer);
    } else if (!question) {
      setAnimatedPoints(0);
    }
  }, [question?.question_id, isLoading]);


  // useEffect(() => {
  //   if (window.history.state === null || !window.history.state.isTrapped) {
  //     window.history.pushState({ isTrapped: true }, "");
  //   }
  // }, []);

  const isSessionActiveRoute = (pathname) =>
    pathname.includes("/play-game");

  useEffect(() => {
    const handlePopState = () => {
      if (isSessionActiveRoute(location.pathname)) {
        if (!trappedRef.current) return; // skip if not trapped yet
        window.history.pushState(null, "", window.location.href); // re-trap
        setPendingNavigation("/choose-topics");
        setPromptEndSession(true);
      }
    };

    // Push a dummy state to trap history (only once)
    if (!trappedRef.current) {
      window.history.pushState(null, "", window.location.href);
      trappedRef.current = true;
    }

    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
      trappedRef.current = false;
    };
  }, [location.pathname]);


  useEffect(() => {
    console.log("Prompt end session changed to:", promptEndSession);
    if (promptEndSession) {
      setShowEndSessionModal(true);
      setPromptEndSession(false);
    }
  }, [promptEndSession]);


  const handleTimeUp = () => {
    if (showFeedback) return;
    console.log("question", question);
    let qid;
    if (question) {
      qid = questionId;
    }
    else {
      qid = localStorage.getItem(`question_${categoryName}`);
      qid = JSON.parse(qid).question_id;
    }
    console.log("timeup");
    api.get(`/api/solo/get_question/${qid}/`)
      .then(response => {
        setAnswer(response.data.answer);
        console.log("answer", response.data.answer,answer);
      })
      .catch(error => {
        console.error("Error fetching question:", error);
      });
    // Unanswered questions get 0 points
    const calculatedPoints = 0;
    setPoints(calculatedPoints);
    setStatus("unanswered");
    setIsCorrect(false);
    setFeedbackMessage("You ran out of time! The correct answer is highlighted.");
    setTriggerApiCall(true);
    setShowFeedback(true);
  };

  const fetchScore = useCallback(() => {
    if (!user?.user_id) {
      setIsLoading(false);
      return;
    }

    let totalDone = false;
    let sessionDone = false;
    api.get(`/api/user/get_total_score/`)
      .then((response) => {
        const totalScore = response.data.total_score || 0;
        //setnewscores({ sessionscore: totalScore });
        setScore(totalScore);
      })
      .catch((error) => {
        console.error("Error fetching total score:", error);
      })
      .finally(() => {
        totalDone = true;
        // if (sessionDone) setIsLoading(false);
      });

    api
      .get(`/api/user/getscore/?content_name=${categoryName}`)
      .then((response) => {
        const sessionScore = response.data.total_score || 0;
        console.log(sessionScore)
        setSessionScore(sessionScore);
      })
      .catch((error) => {
        console.error("Error fetching session score:", error);
      })
      .finally(() => {
        sessionDone = true;
        if (sessionDone) setIsLoading(false);
      });
  }, [user, setScore, categoryName]);

  const fetchQuestion = () => {
    if (!user?.user_id || !categoryName) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    // Prepare difficulty parameter for API call
    const difficultyParam = difficultyLevel === 'random' ? 'all' : difficultyLevel;
    
    api.get("/api/solo/content_only/", { 
      params: { 
        content_name: categoryName,
        difficulty: difficultyParam
      } 
    })
      .then((response) => {
        if (response.data.content) {
          const fetchedQuestion = response.data.content;
          setQuestion(fetchedQuestion);
          console.log("fetchedQuestion", fetchedQuestion);
          //  console.log("questionId", question.question_id);
          setQuestionId(fetchedQuestion.question_id);
          setShowFeedback(false);
          setSelectedAnswer(null);
          setEliminatedOptions(new Set()); // Reset eliminated options for new question
          setTimer(90);
          setNoMoreQuestions(false);
          localStorage.setItem(`question_${categoryName}`, JSON.stringify(fetchedQuestion));
          const sessionId = localStorage.getItem(`sessionId_${categoryName}`);
          console.log("sessionId", sessionId);
          api.post("/api/user/add_content_score/", {
            content_name: categoryName,
            points: 0,
            question_id: fetchedQuestion.question_id,
            category_type: fetchedQuestion.category_type,
            question_status: "unanswered",
            time_spent: 90,
            option_selected: "Not answered",
            session_id: sessionId,
          }).catch((error) => console.error("Error adding content score:", error));

        } else if (response.data.message === "No more questions available") {
          setNoMoreQuestions(true);
          setQuestion(null);
          setQuestionId(null);
        }
      })
      .catch((error) => {
        console.error("Error fetching question:", error);
        setNoMoreQuestions(true);
        setQuestion(null);
        setQuestionId(null);
      })
      .finally(() => setIsLoading(false));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedAnswer || !question) return;

    try {
      const questionData = await api.get(`/api/solo/get_question/${questionId}/`);
      console.log("submit");
      const correctAnswer = questionData.data.answer;

      console.log("correctAnswer", correctAnswer);
      setAnswer(correctAnswer);
      const normalize = (str) =>
        String(str).toLowerCase().replace(/\s+/g, '');
      
      // Get difficulty-based points
      const getDifficultyPoints = (difficultyLevel) => {
        switch (difficultyLevel.toLowerCase()) {
          case 'easy': return 100;
          case 'medium': return 200;
          case 'hard': return 300;
          default: return 100; // Default to easy if difficulty not recognized
        }
      };

      const difficultyPoints = getDifficultyPoints(question.difficulty_level);

      if (question.question_type === "Grid-in") {
        const userAnswer = textAnswer.trim();
        console.log("Raw user input:", userAnswer);
        console.log("Correct answer from DB (raw):", correctAnswer);

        const isCorrectAnswer = normalize(userAnswer) === normalize(correctAnswer);

        console.log("Normalized user:", normalize(userAnswer));
        console.log("Normalized correct:", normalize(correctAnswer));
        console.log("Match result:", isCorrectAnswer);

        setIsCorrect(isCorrectAnswer);
        setStatus(isCorrectAnswer ? "correct" : "incorrect");

        const calculatedPoints = isCorrectAnswer ? difficultyPoints : -difficultyPoints;
        setSessionScore((prevScore) => prevScore + calculatedPoints);
        setScore((prevScore) => prevScore + calculatedPoints);
        setPoints(calculatedPoints);
        const newStreak = isCorrectAnswer ? currentStreak + 1 : 0;
        setCurrentStreak(newStreak);
        setFeedbackMessage(question.feedback || (isCorrectAnswer ? "Correct!" : "Incorrect"));
        setShowFeedback(true);
        setTriggerApiCall(true);
        if (isCorrectAnswer) {
          triggerCorrectEffects();
        } else {
          triggerWrongEffects();
        }
      } else {
        let mappedAnswerValue;
        switch (selectedAnswer) {
          case "A": mappedAnswerValue = question.option_a; break;
          case "B": mappedAnswerValue = question.option_b; break;
          case "C": mappedAnswerValue = question.option_c; break;
          case "D": mappedAnswerValue = question.option_d; break;
          default: mappedAnswerValue = null;
        }
        console.log("mappedAnswerValue", normalize(mappedAnswerValue));
        console.log("correctAnswer", normalize(correctAnswer));
        const isCorrectAnswer = normalize(mappedAnswerValue) === normalize(correctAnswer);

        const newStreak = isCorrectAnswer ? currentStreak + 1 : 0;
        setCurrentStreak(newStreak);

        if (user?.user_id) {
          api.post('/api/user/update_user_streak/', {
            current_streak: newStreak
          }).catch(err => {
            console.error("Failed to update streak:", err);
          });
        }

        setIsCorrect(isCorrectAnswer);
        setStatus(isCorrectAnswer ? "correct" : "incorrect");

        const calculatedPoints = isCorrectAnswer ? difficultyPoints : -difficultyPoints;
        setPoints(calculatedPoints);
          setScore((prevScore) => prevScore + calculatedPoints);
          setSessionScore((prevScore) => prevScore + calculatedPoints);

        setFeedbackMessage(question.feedback || (isCorrectAnswer ? "Correct!" : "Incorrect"));
        setShowFeedback(true);
        setTriggerApiCall(true);
        if (isCorrectAnswer) {
          triggerCorrectEffects();
        } else {
          triggerWrongEffects();
        }
      }
    } catch (error) {
      console.error("Error processing answer:", error);
    }
  };

  useEffect(() => {
    if (triggerApiCall && user && question) {
      const normalizedContentName = categoryName.toLowerCase().replace(/ /g, "_");
      const sessionId = localStorage.getItem(`sessionId_${categoryName}`);
      api.post("/api/user/update_question_score/", {
        question_id: question.question_id,
        points: points,
        question_status: status,
        time_spent: 90 - timer,
        option_selected: selectedAnswer,
        session_id: sessionId,
      }).catch((error) => console.error("Error adding content score:", error));

      api.post("/api/solo/update_session_score/", {
        content_name: categoryName,
        points: points,
      })
        .then(() => fetchScore())
        .catch((error) => console.error("Error updating session score:", error));
      api.post("/api/user/update_score/", {
        content_name: categoryName,
        content_score: points,
        total_score: score,
      })
        .catch((err) => {
          console.error("Error updating total score:", err);
        });
      api.post("/api/user/add_time_category/", {
        time_spent: 90 - timer,
        category_type: getNormalizedCategoryType(categoryName),
      })
        .catch((err) => {
          console.error("Error updating time category:", err);
        });
      setTriggerApiCall(false);
    }
  }, [triggerApiCall, user, question, points, categoryName, status, fetchScore]);

  const handleOptionClick = (optionKey) => {
    if (!showFeedback && !eliminatedOptions.has(optionKey)) {
      setSelectedAnswer(optionKey);
    }
  };

  const handleToggleEliminate = (optionKey, e) => {
    e.stopPropagation(); // Prevent option selection when clicking X
    if (showFeedback) return; // Don't allow elimination after feedback
    
    setEliminatedOptions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(optionKey)) {
        newSet.delete(optionKey);
        // If this option was selected, clear selection
        if (selectedAnswer === optionKey) {
          setSelectedAnswer(null);
        }
      } else {
        newSet.add(optionKey);
        // If this option was selected, clear selection
        if (selectedAnswer === optionKey) {
          setSelectedAnswer(null);
        }
      }
      return newSet;
    });
  };

  const handleNextQuestion = () => {
    setShowSessionStartedMessage(false);
    localStorage.removeItem(`question_${categoryName}`);
    setTextAnswer("");
    setSelectedAnswer(null);
    setShowFeedback(false);
    setIsCorrect(false);
    setStatus("unanswered");
    setPoints(0);
    setFeedbackMessage("");
    setTriggerApiCall(false);
    setTimer(90);
    setEliminatedOptions(new Set()); // Reset eliminated options
      fetchQuestion();
  };

  useEffect(() => {
    console.log("timer", timer, showFeedback, question);
    if (timer > 0 && !showFeedback && question) {
      const interval = setInterval(() => {
        setTimer((prevTimer) => prevTimer - 1);
      }, 1000);
      return () => clearInterval(interval);
    } else if (timer === 0 && !showFeedback && question) {
      console.log("timeup function");
      handleTimeUp();
    }
  }, [timer, showFeedback, question, handleTimeUp]);

  const handleEndSession = (redirectTo = "/choose-topics") => {
    if (!user?.user_id || !categoryName) return;
    const sessionId = localStorage.getItem(`sessionId_${categoryName}`);
    // No need to handle unanswered questions differently - they already get 0 points
    api.post('/api/solo/end_session/', {
      content_name: categoryName,
      session_id: sessionId
    })
      .then(() => {
        localStorage.removeItem(`sessionStarted_${user.user_id}_${categoryName}`);
        localStorage.removeItem(`sessionId_${categoryName}`);
        setIsLoading(false);
        if (redirectTo === "_logout_") {
          clearAuthStorage();
          setUser(null);
          navigate("/");
        }
        else {
          navigate(redirectTo); // 👈 navigate to the intended page
        }
      })
      .catch((error) => {
        console.error("Failed to end session:", error);
        alert("There was an error ending the session. Please try again.");
      });
  };

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      sessionStorage.setItem('wasReloaded', '1');
      event.preventDefault();
      event.returnValue = '';
      return event.returnValue;
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    //window.addEventListener('popstate', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      //window.removeEventListener('popstate', handleBeforeUnload);
    };
  }, []);

  useEffect(() => {
    if (sessionStorage.getItem('wasReloaded') === '1') {
      console.log('wasReloaded');
      sessionStorage.removeItem('wasReloaded');
      localStorage.removeItem('wasReloaded');
      navigate('/options');
    }
  }, [navigate]);

  useEffect(() => {
    // Prevent direct access: only allow if coming from bet or choose topics
    // Only check once on mount to prevent re-navigation
    const allowed = location.state?.allowed;
    if (allowed === false || (allowed === undefined && location.state === null)) {
      console.log('not allowed');
      localStorage.removeItem('wasReloaded');
      sessionStorage.removeItem('wasReloaded');
      navigate('/options', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-sky-50 to-orange-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 p-4 font-zonaProRegular pb-24">
        <Card delay={0} className="text-center max-w-md w-full">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            className="inline-block mb-6"
          >
            <svg className="h-12 w-12" style={{ color: '#34c759' }} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          </motion.div>
          <p className="text-xl text-gray-600 dark:text-gray-100 font-semibold">Loading your question...</p>
        </Card>
      </div>
    );
  }

  if (sessionError) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-sky-50 to-orange-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 p-4 font-zonaProRegular pb-24">
        <Card delay={0} className="text-center max-w-md w-full">
          <h1 className="text-3xl font-zonaProUltra text-red-600 dark:text-red-400 mb-4">Session Error</h1>
          <p className="text-gray-600 dark:text-gray-100 mb-8">{sessionError}</p>
          <motion.button
            onClick={() => navigate('/choose-topics')}
            className="py-3 px-8 bg-[#34c759] text-white text-lg font-zonaProUltra rounded-full hover:shadow-lg hover:shadow-[#34c759]/50 transition-all"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Back to Subjects
          </motion.button>
        </Card>
      </div>
    );
  }

  if (noMoreQuestions) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-sky-50 to-orange-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 p-4 font-zonaProRegular pb-24">
        <Card delay={0} className="text-center max-w-md w-full">
          <h1 className="text-3xl font-zonaProUltra text-gray-800 dark:text-white mb-4">No More Questions</h1>
          <p className="text-gray-600 dark:text-gray-100 mb-8">You've answered all available questions. Check back later!</p>
          <motion.button
            onClick={() => navigate('/choose-topics')}
            className="py-3 px-8 bg-[#34c759] text-white text-lg font-zonaProUltra rounded-full hover:shadow-lg hover:shadow-[#34c759]/50 transition-all"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Back to Subjects
          </motion.button>
        </Card>
      </div>
    );
  }

  const answerOptionKeys = ['A', 'B', 'C', 'D'];
  const currentAnswerOptions = question ? answerOptionKeys.map(key => {
    const value = question['option_' + key.toLowerCase()];
    return value ? { key, value } : null;
  }).filter(opt => opt !== null) : [];

  const getOptionValueByKey = (key) => {
    if (!question || !key) return "Not answered";
    return question['option_' + key.toLowerCase()] || "Not answered";
  };

  const getCorrectAnswerKey = () => {
    if (!question) return '';
    return answerOptionKeys.find(key => question['option_' + key.toLowerCase()] === question.answer) || '';
  };

  // Function to render text with π symbols in font-sans
  const renderTextWithPi = (text) => {
    if (!text.includes('π')) return text;
    
    return text.split('π').map((part, index) => (
      <React.Fragment key={index}>
        {part}
        {index < text.split('π').length - 1 && (
          <span className="font-sans">π</span>
        )}
      </React.Fragment>
    ));
  };

  // Function to get glow color based on difficulty
  const getDifficultyGlow = () => {
    if (!question) return '0 2px 2px rgba(0,0,0,0.1)';
    
    const difficulty = question.difficulty_level?.toLowerCase();
    switch (difficulty) {
      case 'easy':
        return '0 0 30px rgba(34, 197, 94, 0.6), 0 2px 2px rgba(0,0,0,0.1)'; // Green glow
      case 'medium':
        return '0 0 30px rgba(234, 179, 8, 0.6), 0 2px 2px rgba(0,0,0,0.1)'; // Yellow glow
      case 'hard':
        return '0 0 30px rgba(239, 68, 68, 0.6), 0 2px 2px rgba(0,0,0,0.1)'; // Red glow
      default:
        return '0 2px 2px rgba(0,0,0,0.1)';
    }
  };

  // Function to get points at stake based on difficulty
  const getPointsAtStake = () => {
    if (!question) return 0;
    const difficulty = question.difficulty_level?.toLowerCase();
    switch (difficulty) {
      case 'easy': return 100;
      case 'medium': return 200;
      case 'hard': return 300;
      default: return 100;
    }
  };

  // Function to get difficulty color classes
  const getDifficultyColors = () => {
    if (!question) return { 
      bg: 'bg-gray-100 dark:bg-gray-700', 
      text: 'text-gray-800 dark:text-white', 
      border: 'border-gray-300 dark:border-gray-600', 
      glow: 'shadow-gray-500' 
    };
    const difficulty = question.difficulty_level?.toLowerCase();
    switch (difficulty) {
      case 'easy':
        return { 
          bg: 'bg-green-100 dark:bg-green-900/50', 
          text: 'text-green-800 dark:text-green-300', 
          border: 'border-green-500 dark:border-green-600', 
          glow: 'shadow-green-500' 
        };
      case 'medium':
        return { 
          bg: 'bg-yellow-100 dark:bg-yellow-900/50', 
          text: 'text-yellow-800 dark:text-yellow-300', 
          border: 'border-yellow-500 dark:border-yellow-600', 
          glow: 'shadow-yellow-500' 
        };
      case 'hard':
        return { 
          bg: 'bg-red-100 dark:bg-red-900/50', 
          text: 'text-red-800 dark:text-red-300', 
          border: 'border-red-500 dark:border-red-600', 
          glow: 'shadow-red-500' 
        };
      default:
        return { 
          bg: 'bg-gray-100 dark:bg-gray-700', 
          text: 'text-gray-800 dark:text-white', 
          border: 'border-gray-300 dark:border-gray-600', 
          glow: 'shadow-gray-500' 
        };
    }
  };

  return (
    <div className="min-h-screen flex flex-col relative pb-24 pl-18 overflow-hidden bg-gradient-to-br from-sky-50 to-orange-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 font-zonaProRegular">
      {/* Wrong Answer X Mark Animation */}
      <AnimatePresence>
        {showWrongEffect && (
          <motion.div
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: [0, 1.2, 1] }}
            exit={{ opacity: 0, scale: 0 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
            className="fixed inset-0 z-[9999] flex items-center justify-center pointer-events-none"
          >
            <motion.div
              animate={{ 
                rotate: [0, -10, 10, -10, 10, 0],
                scale: [1, 1.1, 1]
              }}
              transition={{ 
                duration: 0.6,
                times: [0, 0.2, 0.4, 0.6, 0.8, 1]
              }}
              className="bg-red-500/20 dark:bg-red-500/30 rounded-full p-12 sm:p-16 md:p-20 backdrop-blur-sm"
            >
              <X 
                size={360} 
                className="text-red-600 dark:text-red-400 w-[360px] h-[360px] sm:w-[400px] sm:h-[400px] md:w-[480px] md:h-[480px]" 
                strokeWidth={8}
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Correct Answer Green Tick Mark Animation */}
      <AnimatePresence>
        {showCorrectEffect && (
          <motion.div
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: [0, 1.2, 1] }}
            exit={{ opacity: 0, scale: 0 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
            className="fixed inset-0 z-[9999] flex items-center justify-center pointer-events-none"
          >
            <motion.div
              animate={{ 
                scale: [1, 1.1, 1]
              }}
              transition={{ 
                duration: 0.6,
                times: [0, 0.2, 0.4, 0.6, 0.8, 1]
              }}
              className="bg-green-500/20 dark:bg-green-500/30 rounded-full p-12 sm:p-16 md:p-20 backdrop-blur-sm"
            >
              <Check 
                size={360} 
                className="text-green-600 dark:text-green-400 w-[360px] h-[360px] sm:w-[400px] sm:h-[400px] md:w-[480px] md:h-[480px]" 
                strokeWidth={8}
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      <div className="relative z-20 px-4 sm:px-6 lg:px-8 py-6">
        {/* Animated Header */}
        <motion.div
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, type: "spring" }}
          className="text-center mb-8"
        >
          <h1
            key={`title-${isDarkMode}-${categoryName}`}
            className="text-4xl sm:text-5xl lg:text-6xl font-bold font-zonaProUltra mb-4 relative inline-block"
            style={{
              color: isDarkMode ? '#ff7a00' : '#212D82',
              background: `linear-gradient(90deg, ${isDarkMode ? '#ff7a00' : '#212D82'} 0%, ${isDarkMode ? '#ff7a00' : '#212D82'} 40%, rgba(255, 122, 0, 0.8) 50%, ${isDarkMode ? '#ff7a00' : '#212D82'} 60%, ${isDarkMode ? '#ff7a00' : '#212D82'} 100%)`,
              backgroundSize: '200% 100%',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              animation: 'shine 10s linear infinite',
              lineHeight: '1.2',
              paddingBottom: '0.25rem'
            }}
          >
                {categoryName === 'Sentence Completion' ? 'Vocabulary In Context' : categoryName}
          </h1>
          {question && (
            <motion.p
              className="text-gray-600 dark:text-gray-200 text-lg font-zonaProRegular"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              Difficulty: <span className="font-semibold capitalize">{question.difficulty_level}</span>
            </motion.p>
          )}
        </motion.div>

        {/* Stats Cards */}
        <div className="w-full max-w-7xl mx-auto mb-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3 md:gap-4">
            <Card delay={0.1} className="text-center">
              <div className="flex flex-col items-center">
                <Clock className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-cyan-500 mb-1 sm:mb-2" />
                <span className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-200 uppercase tracking-wider mb-0.5 sm:mb-1">Timer</span>
                <p className={`text-sm sm:text-base md:text-lg lg:text-xl font-bold font-zonaProUltra ${timer <= 30 && timer > 0 ? 'text-red-500 dark:text-red-400 animate-pulse' : 'text-gray-700 dark:text-white'}`}>
                  {timer}s
                </p>
          </div>
            </Card>
            <Card delay={0.2} className="text-center">
              <div className="flex flex-col items-center">
                <Target className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-green-500 mb-1 sm:mb-2" />
                <span className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-200 uppercase tracking-wider mb-0.5 sm:mb-1">Current Streak</span>
                <p className="text-sm sm:text-base md:text-lg lg:text-xl font-bold text-green-500 dark:text-green-400 font-zonaProUltra">{currentStreak}</p>
            </div>
            </Card>
            <Card delay={0.3} className="text-center">
              <div className="flex flex-col items-center">
                <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-teal-500 dark:text-white mb-1 sm:mb-2" />
                <span className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-200 uppercase tracking-wider mb-0.5 sm:mb-1">Session Score</span>
                <p className="text-sm sm:text-base md:text-lg lg:text-xl font-bold text-teal-500 dark:text-white font-zonaProUltra">{sessionScore}</p>
            </div>
            </Card>
            <Card delay={0.4} className="text-center">
              <div className="flex flex-col items-center">
                <Trophy className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 mb-1 sm:mb-2" style={{ color: '#ff7a00' }} />
                <span className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-200 uppercase tracking-wider mb-0.5 sm:mb-1">Total Score</span>
                <p className="text-sm sm:text-base md:text-lg lg:text-xl font-bold font-zonaProUltra" style={{ color: '#ff7a00' }}>{score}</p>
              </div>
            </Card>
            </div>
            </div>

        <div className="w-full max-w-7xl mx-auto">
          <Card delay={0.5} className="w-full" style={{ borderRadius: '8px', boxShadow: getDifficultyGlow() }}>
            <div className="flex flex-col md:flex-row justify-between items-center mb-6 md:mb-10 pb-4 border-b border-gray-200 dark:border-gray-700 w-full">

        </div>

        {question && !isLoading ? (
          <div className="md:flex md:gap-8">
            <div className="md:w-3/5 lg:w-1/2 space-y-5">
                  <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-1">
                    <h1 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold text-gray-800 dark:text-white font-zonaProUltra">Question:</h1>
                    {question && (() => {
                      const qDiff = question.difficulty_level?.toLowerCase() || 'easy';
                      const difficultyColors = {
                        easy: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 border-green-400 dark:border-green-600',
                        medium: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200 border-yellow-400 dark:border-yellow-600',
                        hard: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 border-red-400 dark:border-red-600'
                      };
                      const colors = difficultyColors[qDiff] || difficultyColors.easy;
                      return (
                        <div className={`inline-block px-3 py-1 rounded-full text-sm font-semibold border-2 ${colors}`}>
                          {qDiff.charAt(0).toUpperCase() + qDiff.slice(1)}
                        </div>
                      );
                    })()}
                    {(() => {
                      const categoryType = question.category_type?.toLowerCase() || '';
                      const isMathCategory = categoryType === 'math' || 
                                            categoryType === 'all math' ||
                                            categoryType.includes('math') ||
                                            categoryName?.toLowerCase().includes('math');
                      return isMathCategory ? (
                        <motion.button
                          onClick={(e) => {
                            e.stopPropagation();
                            setIsCalculatorOpen(!isCalculatorOpen);
                          }}
                          className={`p-2 rounded-lg transition-all duration-200 ${
                            isCalculatorOpen
                              ? 'bg-[#ff9e42] text-white'
                              : isDarkMode
                              ? 'bg-gray-700 hover:bg-gray-600 text-white'
                              : 'bg-gray-200 hover:bg-gray-300 text-gray-800'
                          }`}
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          title="Open Calculator"
                        >
                          <CalculatorIcon className="w-5 h-5" />
                        </motion.button>
                      ) : null;
                    })()}
                  </div>
                  {question.image_name && (
                    <div className="my-4 rounded-lg overflow-hidden shadow max-h-80 flex justify-center items-center bg-gray-50">
                  <ImageViewer fileName={question.image_name} alt={`Question for ${categoryName}`} className="max-h-full max-w-full object-contain" />
                </div>
              )}
                  <p className="text-sm sm:text-base md:text-lg lg:text-xl text-gray-700 dark:text-gray-100 text-left leading-relaxed whitespace-pre-line">
                {renderTextWithPi(question.question_choice || question.content)}
              </p>
              {showFeedback && (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`mt-6 p-5 rounded-lg border-l-4 ${status === 'correct' ? 'border-green-500 bg-green-50 dark:bg-green-900/20' :
                        status === 'incorrect' ? 'border-red-500 bg-red-50 dark:bg-red-900/20' :
                          'border-yellow-500 bg-yellow-50 dark:bg-yellow-900/20'
                        }`}
                    >
                      <h3 className={`text-lg sm:text-xl md:text-2xl lg:text-3xl font-semibold mb-2 font-zonaProUltra ${status === 'correct' ? 'text-green-700 dark:text-green-200' :
                        status === 'incorrect' ? 'text-red-700 dark:text-red-200' :
                          'text-yellow-700 dark:text-yellow-200'
                    }`}>
                    {status === 'correct' ? 'CORRECT!' : status === 'incorrect' ? 'INCORRECT' : "TIME IS UP!"}
                  </h3>
                      <p className={`text-sm sm:text-base md:text-lg lg:text-xl mb-2 ${status === 'correct' ? 'text-green-600 dark:text-green-400' :
                        status === 'incorrect' ? 'text-red-600 dark:text-red-400' :
                          'text-yellow-600 dark:text-yellow-400'
                    }`}>{renderTextWithPi(feedbackMessage)}</p>
                  {status === "unanswered" && question?.feedback && (
                        <p className="text-yellow-700 dark:text-yellow-200 text-sm sm:text-base md:text-lg lg:text-xl mt-2 whitespace-pre-line">{renderTextWithPi(question.feedback)}</p>
                  )}
                      {status !== 'unanswered' && (
                        <div className={`text-sm sm:text-base md:text-lg lg:text-xl font-semibold ${status === 'correct' ? 'text-green-600 dark:text-green-400' : status === 'incorrect' ? 'text-red-600 dark:text-red-400' : 'text-yellow-600 dark:text-yellow-400'}`}>
                      Points: <span className="font-bold">{points > 0 ? `+${points}` : points}</span>
                      {/* Tooltip block */}
                          <div className="text-[10px] sm:text-xs md:text-sm lg:text-base text-gray-500 dark:text-gray-200 mt-1 relative group cursor-help">
                        <span className="underline decoration-dotted">How was this calculated?</span>
                            <div className="absolute z-10 mt-2 w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-100 text-xs p-3 rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none whitespace-normal leading-snug">
                              {isCorrect
                                ? `Correct ${question.difficulty_level} question: +${Math.abs(points)} points`
                                : `Incorrect ${question.difficulty_level} question: -${Math.abs(points)} points`}
                        </div>
                      </div>
                    </div>
                  )}
                    </motion.div>
              )}
            </div>

            <div className="md:w-2/5 lg:w-1/2 md:pl-4 lg:pl-6">
                  <h2 className="text-sm sm:text-base md:text-lg lg:text-xl font-zonaProUltra text-gray-700 dark:text-gray-100 mb-4">
                {showFeedback ? 'Results:' : 'Choose an Answer:'}
              </h2>
              {showFeedback ? (question.question_type === "Grid-in" ? (
                <>
                  {status === "unanswered" ? (
                    answer !== null && (
                      <div className="text-sm sm:text-base md:text-lg lg:text-xl font-semibold p-3 rounded-xl mt-4 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200">
                      Correct Answer: {renderTextWithPi(answer)}
                    </div>
                  )) : (
                    <>
                      <div
                          className={`text-sm sm:text-base md:text-lg lg:text-xl font-semibold p-3 rounded-xl mt-4 ${isCorrect ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200' : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200'}`}
                      >
                        Your Answer: {renderTextWithPi(textAnswer)}
                      </div>
                      {!isCorrect && (
                          <div className="text-sm sm:text-base md:text-lg lg:text-xl font-semibold p-3 rounded-xl mt-3 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200">
                          Correct Answer: {renderTextWithPi(answer)}
                        </div>
                      )}
                    </>
                  )}
                </>
              ) : (
                <div className="space-y-3">
                  {currentAnswerOptions.map((opt) => {
                    if (!opt) return null;
                    const isOptionSelectedByUser = selectedAnswer === opt.key;
                    const isOptionTheActualCorrectAnswer = opt.value === answer;

                    let feedbackOptionClasses = "w-full text-left p-3 sm:p-4 rounded-lg border-2 text-sm sm:text-base md:text-lg lg:text-xl cursor-not-allowed font-medium";

                    if (isOptionSelectedByUser) {
                      if (isCorrect) { // User selected this, and it was correct
                          feedbackOptionClasses += " bg-green-50 dark:bg-green-900/30 border-green-400 dark:border-green-600 text-green-700 dark:text-green-200";
                      } else { // User selected this, and it was incorrect
                          feedbackOptionClasses += " bg-red-50 dark:bg-red-900/30 border-red-400 dark:border-red-600 text-red-700 dark:text-red-200";
                      }
                    } else if (isOptionTheActualCorrectAnswer) { // This is the correct answer, user didn't select it
                      // Highlight with green border and text, but no background fill
                        feedbackOptionClasses += " bg-green-50 dark:bg-green-900/30 border-green-500 dark:border-green-600 text-green-700 dark:text-green-200";
                    } else { // This option was not selected and is not the correct answer (other incorrect options)
                        feedbackOptionClasses += " bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-200 opacity-75";
                    }

                    return (
                      <button
                        type="button"
                        key={opt.key}
                        className={feedbackOptionClasses}
                        disabled={true}
                      >
                        <span className="font-bold mr-2">{opt.key}-)</span>{renderTextWithPi(opt.value)}
                      </button>
                    );
                  })}
                </div>
              )
              ) : (
                <form onSubmit={handleSubmit} className="space-y-3">
                  {question.question_type === "Grid-in" ? (
                    <>
                      <input
                        type="text"
                        value={textAnswer}
                        onChange={(e) => {
                          setTextAnswer(e.target.value);
                          setSelectedAnswer(e.target.value);
                        }}

                          className="w-full p-3 border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-lg bg-white dark:bg-gray-800 text-gray-800 dark:text-white border-gray-300 dark:border-gray-600 disabled:bg-gray-100 dark:disabled:bg-gray-700 disabled:cursor-not-allowed"
                        placeholder="Type your answer here"
                      />
                        <motion.button
                        type="submit"
                        disabled={!selectedAnswer || showFeedback || isLoading}
                          className="mt-6 w-full py-3 px-6 bg-[#34c759] text-white text-sm sm:text-base md:text-lg lg:text-xl font-zonaProUltra rounded-full hover:shadow-lg hover:shadow-gray-900/50 disabled:bg-gray-400 dark:disabled:bg-gray-600 disabled:cursor-not-allowed transition-all"
                          whileHover={{ scale: !selectedAnswer || showFeedback || isLoading ? 1 : 1.02 }}
                          whileTap={{ scale: !selectedAnswer || showFeedback || isLoading ? 1 : 0.98 }}
                      >
                        SUBMIT
                        </motion.button>
                    </>
                  ) : (
                    <>
                      {currentAnswerOptions.map((opt) => {
                        if (!opt) return null;
                        const isEliminated = eliminatedOptions.has(opt.key);
                        const baseClasses = "flex-1 text-left p-3 sm:p-4 rounded-lg border-2 transform hover:scale-[1.02] focus:outline-none text-sm sm:text-base md:text-lg lg:text-xl transition-transform duration-150 ease-in-out";
                        const selectedClasses = "!bg-[#ff9e42] !border-[#ff9e42] text-white font-bold shadow-md ring-2 ring-[#ff9e42]/30";
                        const unselectedClasses = "bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-800 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-700 hover:border-[#ff9e42] focus:border-[#ff9e42] focus:ring-1 focus:ring-[#ff9e42]/20 active:bg-[#ff9e42] active:border-[#ff9e42] active:text-white";
                        const eliminatedClasses = isEliminated ? "opacity-50 cursor-not-allowed line-through" : "";
                        const finalClasses = `${baseClasses} ${selectedAnswer === opt.key ? selectedClasses : unselectedClasses} ${eliminatedClasses}`;

                        return (
                          <div key={opt.key} className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() => handleOptionClick(opt.key)}
                              className={finalClasses}
                              disabled={showFeedback || isEliminated}
                              style={selectedAnswer === opt.key ? { backgroundColor: '#ff9e42', borderColor: '#ff9e42', color: 'white' } : {}}
                            >
                              <span className={`${isEliminated ? 'line-through' : ''}`}>
                                <span className="font-bold mr-2">{opt.key}-</span>{renderTextWithPi(opt.value)}
                              </span>
                            </button>
                            <button
                              type="button"
                              onClick={(e) => handleToggleEliminate(opt.key, e)}
                              className={`p-1.5 sm:p-2 rounded-full transition-all duration-200 flex-shrink-0 ${
                                isEliminated
                                  ? 'bg-red-500 hover:bg-red-600 text-white'
                                  : 'bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-600 dark:text-gray-300'
                              }`}
                              title={isEliminated ? "Restore option" : "Eliminate option"}
                              disabled={showFeedback}
                            >
                              <X className="w-4 h-4 sm:w-5 sm:h-5" />
                            </button>
                          </div>
                        );
                      })}
                        <motion.button
                        type="submit"
                        disabled={!selectedAnswer || showFeedback || isLoading}
                          className="mt-6 w-full text-sm sm:text-base md:text-lg lg:text-xl py-3 px-6 bg-[#34c759] text-white font-zonaProUltra rounded-full hover:shadow-lg hover:shadow-gray-900/50 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:ring-opacity-50 disabled:bg-gray-400 dark:disabled:bg-gray-600 disabled:cursor-not-allowed transition-all"
                          whileHover={{ scale: !selectedAnswer || showFeedback || isLoading ? 1 : 1.02 }}
                          whileTap={{ scale: !selectedAnswer || showFeedback || isLoading ? 1 : 0.98 }}
                      >
                        SUBMIT
                        </motion.button>
                    </>
                  )}
                </form>
              )}
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center min-h-[300px]">
            {isLoading && (
                    <p className="text-sm sm:text-base md:text-lg lg:text-xl text-gray-500 dark:text-gray-200 font-semibold font-zonaProRegular">Loading Question...</p>
            )}
          </div>
        )}
        {showFeedback && !isLoading && (
                <motion.button
            onClick={handleNextQuestion}
                  className="mt-6 text-sm sm:text-base md:text-lg lg:text-xl py-3 px-8 bg-gradient-to-r from-cyan-500 to-teal-500 text-white font-zonaProUltra rounded-full hover:shadow-lg hover:shadow-cyan-500/50 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-opacity-50 transition-all"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
          >
            Next Question
                </motion.button>
        )}
            </Card>
      </div>
      
        {/* Animated Points at Stake Display */}
        {question && !isLoading && (() => {
          const difficulty = question.difficulty_level?.toLowerCase();
          const glowColor = difficulty === 'easy' ? 'rgba(34, 197, 94, 0.6)' : 
                           difficulty === 'medium' ? 'rgba(234, 179, 8, 0.6)' : 
                           difficulty === 'hard' ? 'rgba(239, 68, 68, 0.6)' : 'rgba(0, 0, 0, 0.1)';
          const colors = getDifficultyColors();
          
          return (
          <div className="w-full max-w-7xl mx-auto mt-6 mb-4">
          <motion.div
            initial={{ opacity: 0, y: 50, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{
              duration: 0.6,
              delay: 0.6,
              type: "spring",
              stiffness: 100,
            }}
            className={`relative backdrop-blur-xl rounded-3xl shadow-2xl p-6 w-full transition-all duration-700 ease-in-out ${colors.bg} ${colors.border} border-4`}
            style={{
              boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
            }}
          >
          <style>{`
            @keyframes fadeInUp {
              from {
                opacity: 0;
                transform: translateY(20px);
              }
              to {
                opacity: 1;
                transform: translateY(0);
              }
            }
            @keyframes pulseGlow {
              0%, 100% {
                box-shadow: ${glowColor} 0 0 20px, ${glowColor} 0 0 40px, ${glowColor} 0 0 60px;
              }
              50% {
                box-shadow: ${glowColor} 0 0 30px, ${glowColor} 0 0 60px, ${glowColor} 0 0 90px;
              }
            }
            @keyframes numberPop {
              0%, 100% { transform: scale(1); }
              50% { transform: scale(1.1); }
            }
          `}</style>
          <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-8">
            <div className="text-center md:text-left">
                <p className="text-sm md:text-base uppercase tracking-wider text-gray-600 dark:text-gray-200 mb-2 font-zonaProRegular animate-fade-in">Points at Stake</p>
                <div className="flex items-center justify-center md:justify-start gap-3">
                  <div className={`text-5xl md:text-7xl font-bold ${colors.text} font-zonaProUltra`}
                       style={{ animation: 'numberPop 0.8s ease-out' }}>
                    {animatedPoints}
                  </div>
                  <div className={`text-2xl md:text-3xl ${colors.text} font-zonaProUltra transition-opacity duration-300`}>
                    points
                  </div>
                </div>
              </div>
              
              <div className="hidden md:block w-px h-16 bg-gray-300 dark:bg-gray-600 opacity-50"></div>
              
              <div className="flex flex-col items-center md:items-start gap-2">
                <p className="text-xs md:text-sm uppercase tracking-wider text-gray-600 dark:text-gray-200 font-zonaProRegular">Difficulty</p>
                <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full ${colors.bg} ${colors.border} border-2 transform transition-all duration-300 hover:scale-110`}>
                  <span className={`text-lg md:text-xl font-bold ${colors.text} font-zonaProUltra capitalize`}>
                    {question.difficulty_level}
                  </span>
                </div>
              </div>
              
              <div className="hidden md:block w-px h-16 bg-gray-300 dark:bg-gray-600 opacity-50"></div>
              
              <div className="flex flex-col items-center md:items-start gap-2">
                <p className="text-xs md:text-sm uppercase tracking-wider text-gray-600 dark:text-gray-200 font-zonaProRegular">Reward System</p>
                <div className="flex gap-3 text-sm">
                  <div className="flex items-center gap-1 transform transition-all duration-300 hover:scale-110">
                    <span className="text-green-600 dark:text-green-400 font-bold text-lg animate-pulse">✓</span>
                    <span className="text-gray-700 dark:text-gray-100 font-zonaProRegular font-semibold">+{getPointsAtStake()}</span>
                  </div>
                  <span className="text-gray-400 dark:text-gray-400">|</span>
                  <div className="flex items-center gap-1 transform transition-all duration-300 hover:scale-110">
                    <span className="text-red-600 dark:text-red-400 font-bold text-lg">✗</span>
                    <span className="text-gray-700 dark:text-gray-100 font-zonaProRegular font-semibold">-{getPointsAtStake()}</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
          </div>
          );
        })()}

        <div className="flex flex-col items-center mt-6">
        {showSessionStartedMessage && (
            <motion.p
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-blue-600 dark:text-orange-400 font-semibold mb-2 animate-pulse"
            >
            Session Started!
            </motion.p>
        )}
          <motion.button
          onClick={() => {
            setShowEndSessionModal(true);
            setPromptEndSession(false); // optional: reset global trigger if not from nav
          }}
            className="py-3 px-6 bg-gradient-to-r from-red-600 to-red-700 hover:shadow-lg hover:shadow-red-500/50 text-white text-sm sm:text-base md:text-lg lg:text-xl font-zonaProUltra rounded-full transition-all"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
        >
          End Session
          </motion.button>
      </div>

        <AnimatePresence>
      {showEndSessionModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center"
              onClick={() => {
                cancelledEndSessionRef.current = true;
                setShowEndSessionModal(false);
                setPendingNavigation(null);
              }}
            >
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
                className="backdrop-blur-xl bg-white/95 dark:bg-gray-800/95 rounded-3xl shadow-2xl p-6 w-full max-w-md mx-4 border border-white/20 dark:border-gray-700/50"
              >
                <h2 className="text-sm sm:text-base md:text-lg lg:text-xl font-zonaProUltra text-gray-800 dark:text-white mb-4">End Session?</h2>
                <p className="text-gray-600 dark:text-gray-100 mb-6">
              Are you sure you want to end your current session?
            </p>
            <div className="flex justify-end space-x-4">
                  <motion.button
                onClick={() => {
                  cancelledEndSessionRef.current = true;
                  setShowEndSessionModal(false);
                  setPendingNavigation(null);
                }}
                    className="px-4 py-2 text-sm sm:text-base md:text-lg lg:text-xl bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded font-zonaProUltra text-gray-700 dark:text-white transition-colors"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
              >
                Cancel
                  </motion.button>
                  <motion.button
                onClick={() => {
                  setIsEndingSession(true);
                  setShowEndSessionModal(false);
                  setPromptEndSession(false);
                  const finalRedirect = pendingNavigation || "/choose-topics";
                  handleEndSession(finalRedirect);
                  setBackTriggered(false);
                  setPendingNavigation(null);
                }}
                    className="px-4 py-2 text-sm sm:text-base md:text-lg lg:text-xl bg-gradient-to-r from-red-600 to-red-700 hover:shadow-lg hover:shadow-red-500/50 text-white rounded font-zonaProUltra transition-all"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
              >
                End Session
                  </motion.button>
            </div>
              </motion.div>
            </motion.div>
      )}
        </AnimatePresence>

        {/* Calculator */}
        <Calculator
          isOpen={isCalculatorOpen}
          onClose={() => setIsCalculatorOpen(false)}
          categoryType={question?.category_type}
        />
      </div>
    </div>
  );
};

export default PlayGamePage;
